<template>
  <pf-empty-state v-if="!ready">
    <pf-empty-state-header :title="$t('Stiamo caricando il wizard...')" heading-level="h4">
      <template #icon>
        <pf-empty-state-icon>
          <pf-spinner />
        </pf-empty-state-icon>
      </template>
    </pf-empty-state-header>
    <pf-empty-state-body>
      {{ $t('Ancora pochi istanti e potrai costruire il tuo preventivo.') }}
    </pf-empty-state-body>
  </pf-empty-state>

  <template v-else>
    <wizard-step-clienti v-if="askClienti" />
    <wizard-step-scopo />
    <wizard-step-prodotti-infrastruttura />
    <wizard-step-stack />
    <wizard-step-mail />
    <wizard-step-configurazione />
    <wizard-step-trasferimenti v-if="askClienti" />
    <wizard-step-location />
    <wizard-step-addon />
    <wizard-step-dominio />
    <wizard-step-summary />
    <wizard-step-thankyou />
  </template>
</template>

<script lang="ts">
import { defineComponent, toValue, type PropType, type InjectionKey, computed } from 'vue';
import { asyncComputedList, type AsyncComputedStateObject } from '@common/asyncComputed';
import { provideChildrenTracker, useResettableState } from '@common/use';
import { by } from '@common/utils';
import { $t } from '../../i18n';
import { type NormalizedCatalogProduct } from '../../pages/catalog.vue';

import { WizardStepsTrackerSymbol } from './composition';
import WizardStepScopo from './steps/wizard-step-scopo.vue';
import WizardStepLocation from './steps/wizard-step-location.vue';
import WizardStepProdottiInfrastruttura from './steps/wizard-step-prodotti-infrastruttura.vue';
import WizardStepStack from './steps/wizard-step-stack.vue';
import WizardStepConfigurazione from './steps/wizard-step-configurazione.vue';
import WizardStepTrasferimenti from './steps/wizard-step-trasferimenti.vue';
import WizardStepDominio from './steps/wizard-step-dominio.vue';
import WizardStepAddon from './steps/wizard-step-addon.vue';
import WizardStepMail from './steps/wizard-step-mail.vue';
import WizardStepClienti from './steps/wizard-step-clienti.vue';
import WizardStepSummary from './steps/wizard-step-summary.vue';
import WizardStepThankyou from './steps/wizard-step-thankyou.vue';
import { useLoginStore } from '@/store/login';
import { CompanyResource, MyCustomersResource, OffersResource, type Offer, type MyCustomersAccess } from '@/resources';
import { deployLocations, backupLocations } from '@/common/utils';
import { PRODOTTIORDINI_PROVISIONDB, PRODOTTIORDINI_PROVISIONPHP } from '@common/consts';

export interface Path {
  label: string;
  description: string;
}

export interface Paths {
  hosting?: Path;
  cloud?: Path;
  reseller?: Path;
  host?: Path;
  dominio?: Path | null;
  mail?: Path | null;
  storage?: Path | null;
  altro?: Path | null;
}

export interface ProductCodesByInfraType {
  hosting: Record<OperativeSystems, string[]>;
  reseller: Record<'linux', string[]>;
  host: Record<'linux', string[]>;
  storage: Record<'linux', string[]>;
}

export interface CodiciCloud {
  cpu: string,
  ram: string,
  storage: string,
}

export interface GroupedProducts {
  dominio: {
    items: Product[];
  };
  infrastruttura: {
    items: Product[];
  };
  firewall: {
    items: Product[];
  };
  addon: {
    items: Product[];
  };
  mail: {
    items: Product[];
  };
}

export type Durata = 'Annuale'|'Mensile';
export type ProductPriceInfo = Pick<Product, 'nome' | 'prezzo' | 'prezzo_scontato' | 'durata' | 'utenti'>
export type OperativeSystems = 'linux'|'windows';
export type OpzioniDomainAction = 'registrazione' | 'dns' | undefined;
export type OpzioniStack = 'essential' | 'cockpit' | 'whm' | 'plesk linux' | 'plesk windows' | 'windows' | 'server mail dedicato' | 'mailarchive';
type OpzioniAction ='confirm' | 'pdf' | 'mailme' | 'mailcustomer';

const webServers = ['NGINX', 'Apache'] as const;

const opzioniTecnologie = [
  'Akeneo',
  'Craft CMS',
  'Delphi',
  'Django',
  'Drupal',
  'Elasticsearch',
  'Ionic',
  'Joomla',
  'Kirby',
  'Laravel',
  'Magento 1',
  'Magento 2',
  'MongoDB',
  'Moodle',
  'MySQL',
  'Nextcloud',
  'Node.js',
  'Odoo',
  'Onlyoffice',
  'OroCommerce',
  'PHP',
  'PostgreSQL',
  'PrestaShop',
  'Python',
  'Ruby',
  'Rust',
  'Shopware',
  'Siberian',
  'Statmic',
  'Sulu',
  'Sylius',
  'Symphony',
  'TYPO3',
  'WooCommerce',
  'WordPress',
  'Wordpress multisite',
  '3CX',
] as const;

const opzioniDb: Record<typeof PRODOTTIORDINI_PROVISIONDB[number], string> = {
  'best': $t('Migliore disponibile'),
  'mariadb': 'MariaDB',
  'mysql-8': 'MySQL 8',
  'mysql-57': 'MySQL 5.7',
};
const opzioniPhp: Record<typeof PRODOTTIORDINI_PROVISIONPHP[number], string> = {
  'best': $t('Migliore disponibile'),
  'v8.4': '8.4',
  'v8.3': '8.3',
  'v8.2': '8.2',
  'v8.1': '8.1',
  'v8.0': '8.0',
  'v7.4': '7.4',
  'v7.3': '7.3',
  'v7.2': '7.2',
  'v7.1': '7.1',
  'v7.0': '7.0',
  'v5.6': '5.6',
  'v5.5': '5.5',
  'v5.4': '5.4',
  'v5.3': '5.3',
  'v5.2': '5.2',
};

type ProductInfoWizard = {
  codice: string;
  dominio: string;
  qt?: number;
  os?: string;
  webserver?: 'Apache' | 'NGINX';
  provision_db?: typeof PRODOTTIORDINI_PROVISIONDB[number];
  provision_php?: typeof PRODOTTIORDINI_PROVISIONPHP[number];
  newService?: boolean;
  siteTransfer?: boolean;
  mailTransfer?: boolean;
  notForResale?: boolean;
}

type InfoWizard = {
  action: string;
  title: string;
  nomeServer?: string;
  tecnologie?: string[];
  provision_location?: keyof typeof deployLocations;
  provision_backup_location?: keyof typeof backupLocations;
  note?: string;
  prodotti: ProductInfoWizard[];
}

const codiciProdottiInfrastruttura: ProductCodesByInfraType = {
  hosting: {
    linux: ['HOSMN', 'HOSMRS', 'HOSVEN', 'HOSERT', 'HOSTITA'],
    windows: ['HOSBASW', 'HOSPREMWIN'],
  },
  reseller: {
    linux: ['HOSRESSIL', 'HOSRESGLD', 'HOSRES'],
  },
  host: {
    linux: ['HOSTCORE', 'HOSTGEN'],
  },
  storage: {
    linux: ['STOCOS', 'STOSMART', 'MLARCH100', 'STBCKACR', 'STBCKR1B', 'STBCKIBM', 'STBCKVEE'],
  },
};

const codiciCloud: CodiciCloud = {
  cpu: 'MAVCPU',
  ram: 'MAVRAM',
  storage: 'MAVSTO',
};

const cpuFirewallQt = 2;
const ramFirewallQt = 2;
const storageFirewallQt = 40;

const codiciProdottiMail = ['MLART10', 'MLART20', 'MLART40', 'MLART60', 'MLART80', 'MLART100', 'MLART120'] as const;

const codiciProdottiAddon = {
  registrazionePremium: 'REGPREMY',
  ssl: ['SSLBAS', 'SSLART', 'SSLPREMY', 'SSLWLD'],
  imunify360: ['AHSIM360250', 'AHSIM360500'],
  liteSpeed: 'AHSLITE',
  winServer: 'CLDWINSTD',
  terminalServer: 'CLDRD',
  soloDNS: 'HOSDOM',
  atlas: 'MLARCH100',
  supplierFirewall: 'CLOUDAPPVPN',
  sla: 'SLAPRMC',
  addOn8Ip: 'CLDADD8IP',
  addOn32Ram: 'CLDADD32RAM',
  addOnStorage: {
    'cloud': ['STOSMART'],
    'host': ['STOSMART', 'STOCDS'],
  },
  extraTimeTransferProduct: 'TONEETTRANS',
};
const codiciProdottiSql = ['CLDSQW', 'CLDSQS', 'CLDSQE'] as const;

export type StackInfo = Record<OpzioniStack, {
  codici: string[],
  descrizione: string,
  minRam: number,
  os: OperativeSystems[],
}>;

export type Product = NormalizedCatalogProduct & ProductInfoWizard & {
  codice_wizard: string;
};

export type WizardInjection = {
  readonly webServers: typeof webServers;
  readonly cloudOperativeSystems: string[];
  readonly hostingPaths: Paths;
  readonly cloudPaths: Paths;
  readonly codiceProdottoDominio: string;
  readonly codiciProdottiAddon: typeof codiciProdottiAddon;
  readonly codiciProdottiMail: typeof codiciProdottiMail;
  readonly codiciProdottiSql: typeof codiciProdottiSql;
  readonly stacks: StackInfo;
  readonly codiciCloud: CodiciCloud;
  readonly codiceProdottoImunify: string;
  readonly dominioRegistrabile: boolean;
  readonly customers: MyCustomersAccess[];
  readonly customer: MyCustomersAccess | undefined;
  readonly askClienti: boolean;
  readonly askStack: boolean;
  readonly askFirewall: boolean;
  readonly askNomeServer: boolean;
  readonly askSsl: boolean;
  readonly askAddonStorage: boolean;
  readonly askAddonCpanel: boolean;
  readonly askAddonWindows: boolean;
  readonly askMail: boolean;
  readonly askOs: boolean;
  readonly askEssentialOs: boolean;
  readonly askCockpitConfig: boolean;
  readonly askTecnologie: boolean;
  readonly askConfigurazione: boolean;
  readonly askDb: boolean;
  readonly askPhp: boolean;
  readonly askDomain: boolean;
  readonly askLocation: boolean;
  readonly askLocationBackup: boolean;
  readonly askProdotti: boolean;
  readonly askStorageQt: boolean;
  readonly askRegistrazione: boolean;
  readonly askDurataFatturazione: boolean;
  readonly askTest: boolean;
  readonly askSiteTransfer: boolean;
  readonly askMailTransfer: boolean;
  readonly askExtraTimeTransfer: boolean;
  readonly hidePlusVat: boolean;
  readonly deployLocations: typeof deployLocations;
  readonly backupLocations: typeof backupLocations;
  readonly registraDominio: boolean;
  readonly codiceProdottoSla: string;
  readonly availableDomain: boolean | null | undefined;
  readonly searchedDomain: string | null | undefined;
  readonly groupedProducts: GroupedProducts;
  readonly productsCount: number;
  readonly defaultTitle: string;
  readonly canConfirmOffer: boolean;
  readonly durata: Durata;
  readonly serverCloud: ProductPriceInfo;
  readonly firewall: ProductPriceInfo;
  readonly cpuFirewallQt: number;
  readonly ramFirewallQt: number;
  readonly storageFirewallQt: number;
  readonly initialStep: string;
  currentStep: string;
  freePeriod: boolean;
  notForResale: boolean;
  opzioniTecnologie: typeof opzioniTecnologie;
  opzioniDb: typeof opzioniDb;
  filteredOpzioniPhp: typeof opzioniPhp;
  filteredOpzioniDb: typeof opzioniDb;
  db: typeof PRODOTTIORDINI_PROVISIONDB[number] | undefined;
  php: typeof PRODOTTIORDINI_PROVISIONPHP[number] | undefined;
  path: keyof Paths | undefined;
  sceltaLocationDeploy: keyof typeof deployLocations | undefined;
  sceltaLocationBackup: keyof typeof backupLocations | undefined;
  codiceProdottoStack: string | undefined;
  fatturaMe: boolean;
  askAddonSla: boolean;
  addonSla: boolean;
  addonLiteSpeed: boolean;
  addonImunify: boolean;
  addonTerminalServer: boolean;
  terminalServerQt: number;
  addonAtlas: boolean;
  addOnFirewall: boolean;
  addOn8Ip: boolean;
  addOn32Ram: boolean;
  addOn8IpQt: number;
  addOn32RamQt: number;
  addHostWin: boolean;
  codiceAddonStorage: string,
  qtAddonStorage: number,
  domainAction: 'registrazione' | 'dns' | undefined;
  registrazionePremium: boolean;
  idClienteFinale: number | undefined;
  codiceSsl: string;
  codiceProdottoMail: typeof codiciProdottiMail[number] | '';
  codiceLicenzaSql: typeof codiciProdottiSql[number] | undefined;
  stack: OpzioniStack | null | undefined;
  os: OperativeSystems;
  essentialOs: string | undefined;
  essentialOtherOs: string | undefined;
  cockpitOs: string | undefined;
  cockpitWs: typeof webServers[number] | undefined;
  sceltaProdottoInfrastruttura: string;
  nomeServer: string;
  cpuCloudQt: number;
  ramCloudQt: number;
  storageCloudQt: number;
  storageQt: number;
  codiceProdottoNextcloud: string;
  nomeDominio: string;
  fatturazioneAnnuale: boolean;
  renewAfterFreePeriod: boolean;
  tecnologie: (typeof opzioniTecnologie[number])[];
  newService: boolean | undefined;
  siteTransfer: boolean | undefined;
  mailTransfer: boolean | undefined;
  extraTimeTransfer: boolean;
  action: OpzioniAction | undefined;
  offertaNr: string | number | null;
  title: string | undefined;
  note: string | undefined;
  sending: boolean;
  getProdotto: (codici: string) => Product;
  getProdotti: (codici: readonly string[]) => Product[];
  tldRegistrabile: (domain: string) => boolean;
  totaleByDurata: (durata: Durata, scontato: boolean, group?: keyof GroupedProducts) => number;
  minPrice: (codici: string[]) => Product;
  goToStep: (step: string) => void;
  attiva: (value: boolean) => string;
  siNo: (value: boolean) => string;
  submit: (action: OpzioniAction) => Promise<void>;
  nextStep: () => void;
  previousStep: () => void;
  resetWizard: (dontAsk?: boolean) => void;
  opzioniProdottiInfrastruttura: () => string[];
  customersState: AsyncComputedStateObject;
};

export const WizardInjectionKey = Symbol('WizardInjectionKey') as InjectionKey<WizardInjection>;

export default defineComponent({
  name: 'Wizard',

  components: {
    WizardStepScopo,
    WizardStepLocation,
    WizardStepProdottiInfrastruttura,
    WizardStepStack,
    WizardStepConfigurazione,
    WizardStepTrasferimenti,
    WizardStepDominio,
    WizardStepAddon,
    WizardStepMail,
    WizardStepClienti,
    WizardStepSummary,
    WizardStepThankyou,
  },

  provide() {
    return {
      [WizardInjectionKey as symbol]: this,
    };
  },

  props: {
    searchedDomain: {
      type: String as PropType<string | null | undefined>,
      default: null,
    },
    availableDomain: {
      type: Boolean as PropType<boolean | null | undefined>,
      default: null,
    },
  },

  setup(props) {
    const login = useLoginStore();
    const steps = provideChildrenTracker(WizardStepsTrackerSymbol);

    const stacks: StackInfo = {
      essential: {
        codici: ['AHSESS'],
        descrizione: $t("Questo stack include il monitoraggio essenziale dei parametri vitali e i backup di sicurezza giornalieri, assicurandoti tranquillità e prestazioni stabili per le tue applicazioni."),
        minRam: 1,
        os: ['linux', 'windows'],
      },
      cockpit: {
        codici: ['AHSCCKP'],
        descrizione: $t("Con NGINX, Percona Server for MySQL, Redis e il pannello Cockpit, questo stack offre prestazioni elevate e una gestione semplificata. Include sonde avanzate per un monitoraggio proattivo e gestione personalizzata dei backup, garantendo massima sicurezza e reattività."),
        minRam: 1,
        os: ['linux'],
      },
      whm: {
        codici: ['AHSWHM100', 'AHSWHM250', 'AHSWHM500'],
        descrizione: $t("Perfetto per l'hosting reseller, combina CloudLinux OS e WHM / cPanel per una gestione avanzata. Include supporto specialistico e tutte le funzionalità avanzate del cloud hosting Artera, rendendolo ideale per chi cerca una soluzione completa e affidabile."),
        minRam: 8,
        os: ['linux'],
      },
      'plesk linux': {
        codici: ['AHSPLK1', 'AHSPLK30', 'AHSPLK500'],
        descrizione: $t("Questo stack fornisce un pannello di controllo intuitivo con Plesk per Linux, ottimizzato per la gestione di siti web, applicazioni e server email. Offre strumenti avanzati e supporto specialistico, ideale per professionisti e agenzie che desiderano efficienza."),
        minRam: 1,
        os: ['linux'],
      },
      'plesk windows': {
        codici: ['AHSPLKW1', 'AHSPLKW30', 'AHSPLKW500'],
        descrizione: $t("Questo stack fornisce un pannello di controllo intuitivo con Plesk per Windows, ottimizzato per la gestione di siti web, applicazioni e server email. Offre strumenti avanzati e supporto specialistico, ideale per professionisti e agenzie che desiderano efficienza."),
        minRam: 4,
        os: ['windows'],
      },
      windows: {
        codici: ['AHSWIN'],
        descrizione: $t("Questo stack ti permette di avere il tuo server Windows completamente gestito."),
        minRam: 4,
        os: ['windows'],
      },
      'server mail dedicato': {
        codici: ['AHSDEDMAILY'],
        descrizione: $t("Questo stack ti permette di attivare un server mail dedicato"),
        minRam: 1,
        os: ['linux'],
      },
      mailarchive: {
        codici: ['AHSATLAS'],
        descrizione: $t("Attiva un server di archiviazione email privato con un potente motore di ricerca. Atlas Mail Archive è perfetto per archiviare e recuperare facilmente tutte le email aziendali, garantendo sicurezza e conformità normativa nel flusso di comunicazioni."),
        minRam: 1,
        os: ['linux'],
      },
    };

    const askClienti = computed(() => login.is_reseller);

    const initialStep = askClienti.value ? "WizardStepClienti" : "WizardStepScopo";

    const initialState: Pick<WizardInjection,
        'initialStep'
      | 'freePeriod'
      | 'notForResale'
      | 'currentStep'
      | 'path'
      | 'tecnologie'
      | 'db'
      | 'php'
      | 'sceltaLocationDeploy'
      | 'sceltaLocationBackup'
      | 'os'
      | 'stack'
      | 'essentialOs'
      | 'essentialOtherOs'
      | 'cockpitOs'
      | 'cockpitWs'
      | 'sceltaProdottoInfrastruttura'
      | 'nomeServer'
      | 'cpuCloudQt'
      | 'ramCloudQt'
      | 'storageCloudQt'
      | 'storageQt'
      | 'domainAction'
      | 'nomeDominio'
      | 'registrazionePremium'
      | 'codiceProdottoMail'
      | 'codiceLicenzaSql'
      | 'addonLiteSpeed'
      | 'addonImunify'
      | 'addonTerminalServer'
      | 'terminalServerQt'
      | 'addonAtlas'
      | 'addOnFirewall'
      | 'addOn8Ip'
      | 'addOn32Ram'
      | 'addOn8IpQt'
      | 'addOn32RamQt'
      | 'addHostWin'
      | 'addonSla'
      | 'codiceAddonStorage'
      | 'qtAddonStorage'
      | 'codiceProdottoStack'
      | 'fatturaMe'
      | 'idClienteFinale'
      | 'renewAfterFreePeriod'
      | 'fatturazioneAnnuale'
      | 'newService'
      | 'siteTransfer'
      | 'mailTransfer'
      | 'extraTimeTransfer'
      | 'action'
      | 'codiceSsl'
      | 'title'
      | 'note'
      | 'offertaNr'
    > = {
      initialStep: initialStep,
      currentStep: initialStep,
      freePeriod: false,
      notForResale: false,
      path: undefined,
      tecnologie: [],
      db: 'best',
      php: 'best',
      sceltaLocationDeploy: undefined,
      sceltaLocationBackup: undefined,
      stack: undefined,
      os: 'linux',
      essentialOs: 'AlmaLinux 9',
      essentialOtherOs: undefined,
      cockpitOs: 'AlmaLinux 9',
      cockpitWs: 'NGINX',
      sceltaProdottoInfrastruttura: 'HOSMN',
      nomeServer: '',
      cpuCloudQt: 1,
      ramCloudQt: 1,
      storageCloudQt: 10,
      storageQt: 1,
      domainAction: undefined,
      nomeDominio: props.searchedDomain ?? '',
      registrazionePremium: false,
      codiceProdottoMail: '',
      codiceLicenzaSql: undefined,
      addonLiteSpeed: false,
      addonImunify: false,
      addonTerminalServer: false,
      terminalServerQt: 1,
      addonAtlas: false,
      addOnFirewall: false,
      addOn8Ip: false,
      addOn32Ram: false,
      addOn8IpQt: 1,
      addOn32RamQt: 1,
      addHostWin: false,
      addonSla: false,
      codiceAddonStorage: '',
      qtAddonStorage: 1,
      codiceProdottoStack: undefined,
      fatturaMe: true,
      idClienteFinale: undefined,
      fatturazioneAnnuale: true,
      renewAfterFreePeriod: true,
      action: undefined,
      newService: undefined,
      siteTransfer: undefined,
      mailTransfer: undefined,
      extraTimeTransfer: false,
      codiceSsl: '',
      title: undefined,
      note: undefined,
      offertaNr: null,
    };

    const { state: resettableState, reset: setDefault } = useResettableState(initialState);

    const { state: customersState, result: customers } = asyncComputedList(async() => {
      if (!resettableState.fatturaMe.value) {
        return await new MyCustomersResource().get();
      }
      return [];
    });

    const customer = computed((): MyCustomersAccess | undefined => {
      if (!resettableState.fatturaMe.value && resettableState.idClienteFinale.value) {
        return customers.value.find(c => c.id == resettableState.idClienteFinale.value);
      }

      return {
        id: Number(login.id_azienda),
        ragione_sociale: login.azienda,
        currency: login.currency,
        currency_locked: login.currency_locked,
        vat: login.iva,
        enable_orders: false,
        indirizzo: null,
        provincia: null,
        cap: null,
        citta: null,
        stato: 'CH',
        presented: false,
        partita_iva: null,
        contact_name: null,
        contact_mail: null,
      };
    });

    const customerId = computed(() => customer.value?.id);

    const { state: productsState, result: products } = asyncComputedList(async() => {
      if (customerId.value) {
        return new CompanyResource().priceList(customerId.value).get();
      }
      return [];
    });

    const ready = computed(() => productsState.state === 'success');

    const hostingPaths = {
      dominio: {
        label: $t('Registrazione dominio e DNS'),
        description: $t(
          'Registra il tuo dominio tra oltre 200 estensioni TLD, con DNS avanzato e redirect personalizzati. Un servizio sicuro e flessibile per garantire un\'identità digitale stabile e sempre accessibile, con fatturazione annuale.',
        ),
      },
      mail: {
        label: $t('Solo email'),
        description: $t(
          'Affidati a Only Mail per una gestione email sicura e performante. Caselle illimitate, spazio dedicato e backup automatici assicurano continuità operativa e protezione avanzata per la tua comunicazione aziendale.'
        ),
      },
      hosting: {
        label: $t('Hosting'),
        description:$t('Hosting potente e affidabile con email integrate, backup automatico e certificato SSL. Scegli Linux con LiteSpeed per massima velocità o Windows con Plesk per compatibilità estesa. Configura il tuo piano e lancia il tuo progetto in sicurezza.'),
      },
      ...(login.is_reseller && {
        reseller: {
          label: $t('Hosting Reseller'),
          description: $t('Ottimizza la gestione del tuo hosting con soluzioni reseller scalabili. Account multipli, trasferimento clienti e gestione avanzata. Disponibile in versione self-managed per massima autonomia o managed per supporto completo.'),
        },
      })
    };
    const cloudPaths = {
      cloud: {
        label: $t('Cloud server'),
        description:$t('Configura il tuo cloud server dedicato per siti web, e-commerce e applicazioni. Un\'infrastruttura scalabile e ottimizzata sulle tue esigenze, con massima flessibilità operativa e sicurezza avanzata.'),
      },
      host: {
        label: $t('Host server'),
        description: $t('Affidati a un’infrastruttura hardware dedicata e performante per applicazioni aziendali e servizi business-critical. Risorse esclusive, sicurezza avanzata e scalabilità garantiscono continuità operativa e massima affidabilità con storage all-flash, firewall e VPN personalizzate.'),
      },
      storage: {
        label: $t('Storage & Backup'),
        description: $t(
          'Attiva subito spazio di archiviazione sicuro e ridondato per proteggere i tuoi dati. Soluzioni flessibili per backup gestito, storage cloud e archiviazione di file critici, con massima affidabilità e recupero rapido.',
        ),
      },
      altro: {
        label: $t('Altro'),
        description: $t(
          'Licenze software, certificati SSL, archiviazione email Atlas e altre soluzioni professionali per migliorare e proteggere la tua infrastruttura digitale.',
        ),
      },
    };

    return {
      login,
      steps,
      stacks,
      ...resettableState,
      setDefault,
      products,
      customers,
      customersState,
      customer,
      ready,
      hostingPaths,
      cloudPaths,
      askClienti,
    };
  },

  data() {
    return {
      sending: false,
      freePeriod: false,
      webServers,
      codiciCloud,
      codiciProdottiMail,
      codiciProdottiSql,
      codiciProdottiAddon,
      opzioniTecnologie,
      opzioniDb,
      opzioniPhp,
      deployLocations,
      backupLocations,
      cpuFirewallQt,
      ramFirewallQt,
      storageFirewallQt,
    };
  },

  computed: {
    durata(): Durata {
      return this.fatturazioneAnnuale ? 'Annuale' : 'Mensile';
    },

    askOs() {
      return (this.path === 'hosting' || this.path === 'cloud');
    },

    askEssentialOs() {
      return this.askStack && this.stack === 'essential' && this.os === 'linux';
    },

    askCockpitConfig() {
      return this.askStack && this.stack === 'cockpit';
    },

    askLocation() {
      return this.path !== 'dominio' && this.path !== 'altro';
    },

    askLocationBackup() {
      return this.path !== 'dominio'  && this.path !== 'altro' && this.path !== 'storage';
    },

    askProdotti() {
      return this.path !== 'dominio' && this.path !== 'altro' && this.path !== 'mail';
    },

    askStorageQt() {
      return this.path === 'storage';
    },

    askDomain() {
      return (this.path !== 'cloud' || (this.askMail && this.codiceProdottoMail)) && this.path !== 'host';
    },

    askRegistrazione() {
      return !['dominio', 'storage'].includes(this.path ?? '');
    },

    askSsl() {
      return this.path !== 'dominio' && this.path !== 'reseller' && (this.path !== 'cloud' || (this.askMail && this.codiceProdottoMail)) && this.path !== 'host';
    },

    askAddonCpanel() {
      return this.path === 'cloud' && this.stack === 'whm';
    },

    askAddonWindows() {
      return this.path === 'cloud' && this.os === 'windows' && this.stack !=='plesk windows';
    },

    askStack() {
      return this.path === 'cloud';
    },

    askFirewall() {
      return this.path === 'cloud' || this.path === 'host';
    },

    askNomeServer() {
      return this.path === 'cloud' || this.path === 'host';
    },

    askDb() {
      return this.os === 'linux' && (this.path === 'hosting' || this.askStack && this.stack && ['essential', 'cockpit', 'whm', 'plesk linux'].includes(this.stack));
    },

    askPhp() {
      return this.os === 'linux' && (this.path === 'hosting' || this.askStack && this.stack && ['essential', 'cockpit', 'whm', 'plesk linux'].includes(this.stack));
    },

    askTecnologie() {
      return this.os === 'linux' && (this.path === 'hosting' || this.path === 'cloud');
    },

    askConfigurazione() {
      return (this.askDb || this.askPhp || this.askTecnologie) && (this.path !== 'cloud' || (this.stack && !['essential', 'cockpit'].includes(this.stack)) || (this.stack === 'essential' && this.essentialOs !== '3CX') || (this.stack === 'cockpit' && this.cockpitOs !== '3CX') );
    },

    askTest() {
      return this.login.is_reseller && this.path !== 'dominio' && this.path !== 'altro' && this.path !== 'host';
    },

    askMail() {
      return (this.path === 'cloud' && (!this.stack || this.stack === 'essential' || this.stack === 'cockpit' || this.stack === 'windows')) || this.path === 'mail' || (this.path === 'hosting' && this.os === 'windows');
    },

    askDurataFatturazione() {
      return this.path !== 'dominio';
    },

    askSiteTransfer() {
      return this.login.is_reseller && this.path === 'hosting' && this.os === 'linux';
    },

    askMailTransfer() {
      return this.login.is_reseller && ((this.path === 'hosting' && this.os === 'linux') || (this.path === 'hosting' && this.askMail && this.codiceProdottoMail) || this.path === 'mail');
    },

    askExtraTimeTransfer() {
      return this.newService === false && ((this.askSiteTransfer && this.siteTransfer) || (this.askMailTransfer && this.mailTransfer));
    },

    askAddonSla() {
      return this.path !== 'dominio' && this.path !== 'altro';
    },

    askAddonStorage() {
      return this.path === 'cloud' || this.path === 'host';
    },

    cloudOperativeSystems() {
      const os = ['AlmaLinux 9', 'AlmaLinux 8', 'Debian', 'Ubuntu'];
      if (this.stack === 'essential') {
        return os.concat('3CX');
      }
      return os;
    },

    hidePlusVat() {
      if (this.fatturaMe && this.login.iva < 0.01) {
        return true;
      }
      if (this.customer && this.customer.vat < 0.01) {
        return true;
      }

      return false;
    },

    registraDominio() {
      return ((this.searchedDomain && this.searchedDomain === this.nomeDominio ) || this.path === 'dominio' || this.domainAction === 'registrazione') && !!this.nomeDominio;
    },

    filteredOpzioniPhp() {
      if (this.askPhp && this.stack === "cockpit" && this.cockpitOs === 'AlmaLinux 9') {
        return Object.fromEntries(
          Object.entries(this.opzioniPhp).filter(([key]) => key >= 'v7.4' || key === 'best'),
        );
      }
      if (this.askPhp && this.stack === "cockpit" && this.cockpitOs === 'AlmaLinux 8') {
        return Object.fromEntries(
          Object.entries(this.opzioniPhp).filter(([key]) => key >= 'v5.4' || key === 'best'),
        );
      }

      return this.opzioniPhp;
    },

    filteredOpzioniDb() {
      if (this.askDb && this.path === "hosting" && this.sceltaProdottoInfrastruttura === "HOSTITA") {
        return Object.fromEntries(
          Object.entries(this.opzioniDb).filter(([key]) => key === 'mariadb'),
        );
      }

      return this.opzioniDb;
    },

    estensioneDominio() {
      const tld = this.tldRegistrabile(this.nomeDominio);
      if (tld !== false) {
        return tld;
      }

      const v = this.nomeDominio.split('.');
      return v[v.length - 1];
    },

    tldRegistrabili() {
      const tld: Record<string, string> = {};
      const domini = this.products.filter(p => p.tld);
      for (const dom of domini) {
        if (dom.tld) {
          tld[dom.tld] = dom.codice;
        }
      }
      return tld;
    },

    dominioRegistrabile() {
      return this.tldRegistrabile(this.nomeDominio) !== false;
    },

    codiceProdottoDominio() {
      return this.tldRegistrabili[this.estensioneDominio];
    },

    codiceProdottoImunify() {
      switch (this.codiceProdottoStack) {
        case 'AHSWHM100': return 'AHSIM360250';
        case 'AHSWHM250': return 'AHSIM360250';
          break;
        case 'AHSWHM500': return 'AHSIM360500';
        default: return 'AHSIM3601';
      };
    },

    canConfirmOffer() {
      return this.fatturaMe ? this.login.enable_orders : this.customer?.enable_orders ?? false;
    },

    defaultTitle() {
      return $t('Offerta per servizi internet - {0}', [this.askDomain ? this.nomeDominio : this.nomeServer]);
    },

    productsCount() {
      let c = 0;
      for (const i of Object.values(this.groupedProducts)) {
        c += i.items.length;
      }
      return c;
    },

    serverCloud() {
      const prezzo =
        this.getProdotto(this.codiciCloud.cpu).prezzo * this.cpuCloudQt +
        this.getProdotto(this.codiciCloud.ram).prezzo * this.ramCloudQt +
        this.getProdotto(this.codiciCloud.storage).prezzo * this.storageCloudQt +
        (this.os === 'windows' ? this.getProdotto(this.codiciProdottiAddon.winServer).prezzo * Math.round(this.cpuCloudQt / 2) : 0);

      const prezzo_scontato =
        this.getProdotto(this.codiciCloud.cpu).prezzo_scontato * this.cpuCloudQt +
        this.getProdotto(this.codiciCloud.ram).prezzo_scontato * this.ramCloudQt +
        this.getProdotto(this.codiciCloud.storage).prezzo_scontato * this.storageCloudQt +
        (this.os === 'windows' ? this.getProdotto(this.codiciProdottiAddon.winServer).prezzo_scontato * Math.round(this.cpuCloudQt / 2) : 0);

      return {
        nome: 'Server personalizzato',
        prezzo,
        prezzo_scontato,
        durata: this.durata,
      };
    },

    firewall() {
      if (this.path === 'host') {
        return this.getProdotto(this.codiciProdottiAddon.supplierFirewall);
      }
      const prezzo =
        this.getProdotto(this.codiciCloud.cpu).prezzo * cpuFirewallQt +
        this.getProdotto(this.codiciCloud.ram).prezzo * ramFirewallQt +
        this.getProdotto(this.codiciCloud.storage).prezzo * storageFirewallQt +
        this.getProdotto(this.stacks.essential.codici[0]).prezzo;

      const prezzo_scontato =
        this.getProdotto(this.codiciCloud.cpu).prezzo_scontato * cpuFirewallQt +
        this.getProdotto(this.codiciCloud.ram).prezzo_scontato * ramFirewallQt +
        this.getProdotto(this.codiciCloud.storage).prezzo_scontato * storageFirewallQt +
        this.getProdotto(this.stacks.essential.codici[0]).prezzo_scontato;

      return {
        nome: 'Firewall Cloud',
        prezzo,
        prezzo_scontato,
        durata: this.durata,
      };
    },

    groupedProducts() {
      const prodotti: GroupedProducts = {
        dominio: {
          items: [],
        },
        infrastruttura: {
          items: [],
        },
        firewall: {
          items: [],
        },
        addon: {
          items: [],
        },
        mail: {
          items: [],
        },
      };

      // Prodotti dominio

      if (this.registraDominio && this.dominioRegistrabile) {
        prodotti.dominio.items.push({
          ...this.getProdotto(this.codiceProdottoDominio),
          dominio: this.nomeDominio,
        });

        if (this.registrazionePremium) {
          prodotti.dominio.items.push({
            ...this.getProdotto(this.codiciProdottiAddon.registrazionePremium),
            dominio: this.nomeDominio,
          });
        }
      } else if (this.domainAction === 'dns') {
        prodotti.dominio.items.push({
          ...this.getProdotto(this.codiciProdottiAddon.soloDNS),
          dominio: this.nomeDominio,
        });
      }

      // Prodotti infrastruttura
      if (this.askProdotti && this.path !== 'cloud' && this.sceltaProdottoInfrastruttura) {
        const infraProd = {
          ...this.getProdotto(this.sceltaProdottoInfrastruttura),
          qt: this.askStorageQt ? this.storageQt : 1,
          dominio: this.askNomeServer ? `${this.nomeServer}.artera.farm` : this.nomeDominio,
          provision_db: this.askDb ? this.db : undefined,
          provision_php: this.askPhp ? this.php : undefined,
        };

        if (this.askSiteTransfer || this.askMailTransfer) {
          infraProd.newService = this.newService;

          if (this.askSiteTransfer) {
            infraProd.siteTransfer = this.siteTransfer;
          }
          if (this.askMailTransfer) {
            infraProd.mailTransfer = this.mailTransfer;
          }
        }

        prodotti.infrastruttura.items.push(infraProd);

        if (this.askExtraTimeTransfer && this.extraTimeTransfer) {
          prodotti.infrastruttura.items.push({
              ...this.getProdotto(this.codiciProdottiAddon.extraTimeTransferProduct),
            qt: 1,
            dominio: this.nomeDominio,
          });
        }
      } else if (this.askProdotti && this.path === 'cloud') {
        prodotti.infrastruttura.items.push({
          ...this.getProdotto(codiciCloud.cpu),
          qt: this.cpuCloudQt,
          dominio: `${this.nomeServer}.artera.farm`,
        });
        prodotti.infrastruttura.items.push({
          ...this.getProdotto(codiciCloud.ram),
          qt: this.ramCloudQt,
          dominio: `${this.nomeServer}.artera.farm`,
        });
        prodotti.infrastruttura.items.push({
          ...this.getProdotto(codiciCloud.storage),
          qt: this.storageCloudQt,
          dominio: `${this.nomeServer}.artera.farm`,
        });

        if (this.os === 'windows') {
          prodotti.infrastruttura.items.push({
            ...this.getProdotto(this.codiciProdottiAddon.winServer),
            dominio: `${this.nomeServer}.artera.farm`,
            qt: Math.round(this.cpuCloudQt / 2),
          });
        }

        if (this.askStack && this.codiceProdottoStack) {
          const stack = {
            ...this.getProdotto(this.codiceProdottoStack),
            dominio: `${this.nomeServer}.artera.farm`,
          };
          if (this.askEssentialOs) {
            stack.os = this.essentialOs === 'altro' ? this.essentialOtherOs : this.essentialOs;
          } else if (this.askCockpitConfig && this.cockpitWs && this.cockpitOs) {
            stack.os = this.cockpitOs;
            stack.webserver = this.cockpitWs;
          } else if (this.askOs) {
            stack.os = this.os;
          }
          stack.provision_db = this.askDb ? this.db : undefined;
          stack.provision_php = this.askPhp ? this.php : undefined;

          prodotti.infrastruttura.items.push(stack);
        }

        if (this.askAddonCpanel) {
          if (this.addonImunify && this.codiceProdottoImunify) {
            prodotti.infrastruttura.items.push({
              ...this.getProdotto(this.codiceProdottoImunify),
              dominio: `${this.nomeServer}.artera.farm`,
            });
          }
          if (this.addonLiteSpeed) {
            prodotti.infrastruttura.items.push({
              ...this.getProdotto(this.codiciProdottiAddon.liteSpeed),
              dominio: `${this.nomeServer}.artera.farm`,
            });
          }
        }

        if (this.askAddonWindows) {
          if (this.addonTerminalServer) {
            prodotti.infrastruttura.items.push({
              ...this.getProdotto(this.codiciProdottiAddon.terminalServer),
              dominio: `${this.nomeServer}.artera.farm`,
              qt: this.terminalServerQt,
            });
          }

          if (this.codiceLicenzaSql) {
            prodotti.infrastruttura.items.push({
              ...this.getProdotto(this.codiceLicenzaSql),
              qt: Math.round(this.cpuCloudQt / 2),
              dominio: `${this.nomeServer}.artera.farm`,
            });
          }
        }
      }

      // Se sto comprando un host devo aggiugnere per forza un cockpit
      if (this.path === 'host') {
        prodotti.infrastruttura.items.push({
          ...this.getProdotto(this.stacks.cockpit.codici[0]),
          dominio: `${this.nomeServer}.artera.farm`,
        });

        if (this.addOn32Ram) {
          prodotti.infrastruttura.items.push({
            ...this.getProdotto(this.codiciProdottiAddon.addOn32Ram),
            dominio: `${this.nomeServer}.artera.farm`,
            qt: this.addOn32RamQt,
          });
        }
        if (this.addOn8Ip) {
          prodotti.infrastruttura.items.push({
            ...this.getProdotto(this.codiciProdottiAddon.addOn8Ip),
            dominio: `${this.nomeServer}.artera.farm`,
            qt: this.addOn8IpQt,
          });
        }
        if (this.addHostWin) {
          prodotti.infrastruttura.items.push({
            ...this.getProdotto(this.codiciProdottiAddon.winServer),
            dominio: `${this.nomeServer}.artera.farm`,
            qt: Math.round(Number(this.getProdotto(this.sceltaProdottoInfrastruttura).core) / 2),
          });
        }
      }

      if (this.askFirewall && this.addOnFirewall) {
        if (this.path === 'host') {
          prodotti.firewall.items.push({
            ...this.getProdotto(this.codiciProdottiAddon.supplierFirewall),
            dominio: `${this.nomeServer}-firewall.artera.farm`,
          });
        } else {
          prodotti.firewall.items.push({
            ...this.getProdotto(codiciCloud.cpu),
            qt: cpuFirewallQt,
            dominio: `${this.nomeServer}-firewall.artera.farm`,
          });
          prodotti.firewall.items.push({
            ...this.getProdotto(codiciCloud.ram),
            qt: ramFirewallQt,
            dominio: `${this.nomeServer}-firewall.artera.farm`,
          });
          prodotti.firewall.items.push({
            ...this.getProdotto(codiciCloud.storage),
            qt: storageFirewallQt,
            dominio: `${this.nomeServer}-firewall.artera.farm`,
          });
          prodotti.firewall.items.push({
            ...this.getProdotto(this.stacks.essential.codici[0]),
            dominio: `${this.nomeServer}-firewall.artera.farm`,
            os: 'OPNSense',
          });
        }
      }

      if (this.askAddonStorage && this.codiceAddonStorage) {
        prodotti.addon.items.push({
          ...this.getProdotto(this.codiceAddonStorage),
          dominio: `${this.nomeServer}.artera.farm`,
          qt: this.qtAddonStorage,
        });
      }

      // Prodotti AddOn
      if (this.askSsl && this.codiceSsl) {
        prodotti.addon.items.push({
          ...this.getProdotto(this.codiceSsl),
          dominio: this.nomeDominio,
        }); // Prodotto unicamente annuale
      }
      if (this.askAddonSla && this.addonSla) {
        prodotti.addon.items.push({
          ...this.getProdotto(this.codiciProdottiAddon.sla),
          dominio: this.askDomain && this.nomeDominio ? this.nomeDominio : `${this.nomeServer}.artera.farm`,
        });
      }

      if (this.path === 'altro' && this.addonTerminalServer) {
        prodotti.addon.items.push({
          ...this.getProdotto(this.codiciProdottiAddon.terminalServer),
          dominio: this.nomeDominio,
          qt: this.terminalServerQt,
        });
      }

      if (this.path === 'altro' && this.addonAtlas) {
        prodotti.addon.items.push({
          ...this.getProdotto(this.codiciProdottiAddon.atlas),
          dominio: this.nomeDominio,
        });
      }

      // Prodotti mail
      if (this.askMail && this.codiceProdottoMail) {
        const mailProd = {
          ...this.getProdotto(this.codiceProdottoMail),
          dominio: this.nomeDominio,
        };

        if (this.askMailTransfer) {
          mailProd.newService = this.newService;
          mailProd.mailTransfer = this.mailTransfer;
        }
        prodotti.mail.items.push(mailProd);
      }

      return prodotti;
    },
  },

  watch: {
    fatturaMe(v) {
      if (v) {
        this.idClienteFinale = undefined;
      }
    },

    stack(v: OpzioniStack) {
      if (this.askStack && v) {
        if (this.stacks[v].codici.length === 1) {
          this.codiceProdottoStack = this.stacks[v].codici[0];
        } else {
          this.codiceProdottoStack = undefined;
        }
      } else {
        this.codiceProdottoStack = undefined;
      }
    },
  },

  mounted() {
    this.setDefault();
  },

  methods: {
    opzioniProdottiInfrastruttura() {
      if (!this.path || this.path === 'dominio' || this.path === 'cloud' || this.path === 'mail' || this.path === 'altro') {
        return [];
      }

      if (this.path === 'storage' || this.path === 'reseller' || this.path === 'host') {
        return codiciProdottiInfrastruttura[this.path]['linux'];
      }

      return codiciProdottiInfrastruttura[this.path][this.os];
    },

    tldRegistrabile(domain: string) {
      for (const tld of Object.keys(this.tldRegistrabili).sort(by(s => s.length, true))) {
        if (domain.endsWith(`.${tld}`)) {
          return tld;
        }
      }
      return false;
    },

    nextStep() {
      for (
        let i = this.steps.findIndex(x => x.$options.name === this.currentStep) + 1;
        i < this.steps.length;
        i++
      ) {
        const step = this.steps[i];
        const enabled = step.isEnabled ?? toValue(step.$.exposed?.isEnabled);
        if (enabled && step.$options.name) {
          this.currentStep = step.$options.name;

          const page = document.querySelector('.pf-v6-c-page__main');
          page?.scroll({
            top: 0,
            behavior: 'smooth',
          })

          return;
        }
      }
    },

    previousStep() {
      for (let i = this.steps.findIndex(x => x.$options.name === this.currentStep) - 1; i >= 0; i--) {
        const step = this.steps[i];
        const enabled = step.isEnabled ?? toValue(step.$.exposed?.isEnabled);
        if (enabled && step.$options.name) {
          this.currentStep = step.$options.name;

          const page = document.querySelector('.pf-v6-c-page__main');
          page?.scroll({
            top: 0,
            behavior: 'smooth',
          })
          return;
        }
      }
    },

    resetWizard(dontAsk: boolean) {
      const r = dontAsk || confirm($t('Sei sicuro di voler resettare tutte le tue scelte e tornare al primo step?'));
      if (r) {
        this.setDefault();
      }
    },

    getProdotto(codice: string) {
      if (this.products) {
        const prodotto = this.products.find(
          p => (p.group_code === codice && p.durata === this.durata) || p.codice === codice,
        ) as Product;
        if (prodotto) {
          prodotto.prezzo = this.customer?.currency === 'EUR' ? prodotto.prezzo_eur : prodotto.prezzo_chf;
          prodotto.prezzo_scontato = this.customer?.currency === 'EUR' ? prodotto.prezzo_scontato_eur : prodotto.prezzo_scontato_chf;
          prodotto.prezzo_customer = this.login.currency == 'EUR' ? prodotto.prezzo_clienti_eur : prodotto.prezzo_clienti_chf;
          prodotto.codice_wizard = codice;
          return prodotto;
        }
      }
      return { codice } as Product;
    },

    getProdotti(codici: readonly string[]) {
      const prodotti = [];
      for (const codice of codici) {
        prodotti.push(this.getProdotto(codice));
      }
      return prodotti;
    },

    minPrice(codici: string[]) {
      const prodotti = this.getProdotti(codici);
      return prodotti.reduce((a, b) => a.prezzo_scontato < b.prezzo_scontato ? a : b);
    },

    totaleByDurata(durata: Durata, scontato: boolean, group?: keyof GroupedProducts) {
      const products = !!group ?
        this.groupedProducts[group].items :
        Object.values(this.groupedProducts)
          .flatMap(c => c.items) as Product[];

      return products
        .filter(p => p.durata === durata)
        .reduce((t, p) => t + ((this.fatturaMe && scontato ? p.prezzo_scontato : p.prezzo) * (p.qt ?? 1)), 0);
    },

    goToStep(step: string) {
      this.currentStep = step;
    },

    attiva(value: boolean) {
      return value ? $t('Attiva') : $t('Non attiva');
    },

    siNo(value: boolean) {
      return value ? $t('Sì') : $t('No');
    },

    createOffer(action: OpzioniAction) {
      if (!this.login.id_azienda || !this.customer) {
        return;
      }

      let id_cliente;
      if (!this.fatturaMe) {
        if (!this.idClienteFinale) {
          throw new Error('Nessun cliente finale selezionato');
        }
        id_cliente = this.idClienteFinale;
      } else {
        id_cliente = this.login.id_azienda;
      }

      const data: Partial<Offer> & InfoWizard = {
        action: action,
        currency: this.customer.currency,
        title: this.title ?? this.defaultTitle,
        free_period: this.freePeriod ? 30 : 0,
        id_azienda: Number(id_cliente),
        prodotti: [],
      };

      if (this.login.is_reseller && this.freePeriod) {
        data.testing_mode = this.renewAfterFreePeriod ? 'rinnova' : 'disdici';
      }

      if (this.askTecnologie) {
        data.tecnologie = this.tecnologie.length > 0 ? this.tecnologie : ['Nessuna'];
      }

      if (this.askLocation && this.sceltaLocationDeploy) {
        data.provision_location = this.sceltaLocationDeploy;
      }

      if (this.askLocationBackup && this.sceltaLocationBackup) {
        data.provision_backup_location = this.sceltaLocationBackup;
      }

      if (!this.fatturaMe) {
        data.id_reseller = Number(this.login.id_azienda);
      }

      if (this.note) {
        data.note = this.note;
      }

      for (const prodotto of Object.values(this.groupedProducts)
        .map(c => c.items as Product[])
        .flat()) {
        data.prodotti.push({
          codice: prodotto.codice,
          qt: prodotto.qt ?? 1,
          dominio: prodotto.dominio,
          os: prodotto.os,
          webserver: prodotto.webserver,
          provision_db: prodotto.provision_db,
          provision_php: prodotto.provision_php,
          newService: prodotto.newService,
          siteTransfer: prodotto.siteTransfer,
          mailTransfer: prodotto.mailTransfer,
          notForResale: this.login.is_reseller && this.notForResale,
        });
      }

      const offer = new OffersResource();
      return offer.save(data);
    },

    async submit(action: OpzioniAction) {
      this.action = action;

      if (this.sending || this.productsCount === 0) {
        return;
      }
      this.sending = true;

      try {
        const response = await this.createOffer(action);
        if (!response) {
          this.sending = false;
          return;
        }
        this.offertaNr = response.id_offerta;

        if (action == 'confirm') {
          this.$router.push({
            name: 'confirm_offer',
            params: { id: response.id_offerta },
          });
        } else {
          this.nextStep();
        }
      } finally {
        this.sending = false;
      }
    },
  },
});
</script>
